import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../../assets/images";
import { useTranslate } from "../../../context/TranslationContext";
import { useGetPurchasedProductQuery } from "../../../redux/Apis/DigitalProducts";
import { useGetCurrentSubscriptionQuery } from "../../../redux/Apis/Packages";
import { useUpdateUserMutation } from "../../../redux/Apis/User";
import { handleCart } from "../../../redux/Slices/General";
import { getBasketKey } from "../../../utils/basket";
import {
  convertPrice,
  formatCurrency,
  getConfigByCountryCode,
} from "../../../utils/localization";
import Button from "../../button";
import RoleSwitchSuccessModal from "../../modal/RoleSwitchSuccessModal";
import SwitchToBothRoleModal from "../../modal/SwitchToBothRoleModal";

const HeroSectionTemplateCard = ({
  file,
  title,
  title_hook,
  subtitle,
  access_type,
  membership_type,
  duration,
  pricing,
  _id,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslate("templatebanner");
  const { t: cartText } = useTranslate("cart");
  const dispatch = useDispatch();
  const { cart } = useSelector((state) => state.generalSlice);
  const lang = useSelector((state) => state.language.lang);
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const [isHovering, setIsHovering] = useState(false);
  const [displayPrice, setDisplayPrice] = useState("");
  const [showRoleSwitchModal, setShowRoleSwitchModal] = useState(false);
  const [showRoleSwitchSuccessModal, setShowRoleSwitchSuccessModal] =
    useState(false);
  const {
    data: current_subscription,
    isSuccess: currentSuccess,
    refetch,
  } = useGetCurrentSubscriptionQuery({ lang }, { skip: !isLoggedIn });
  const { data: purchasedProductsRes } = useGetPurchasedProductQuery(
    { params: { lang } },
    { skip: !isLoggedIn },
    { refetchOnMountOrArgChange: true },
  );

  const [updateUser, { isLoading: isLoadingUpdate }] = useUpdateUserMutation();

  const purchasedProducts = purchasedProductsRes?.data || [];

  const isProductPurchased = purchasedProducts.some(
    (item) => item?.product?._id === _id && item.type === "template",
  );

  useEffect(() => {
    const updatePrice = async () => {
      if (!pricing) return;

      const selectedCountry =
        user?.country || localStorage?.getItem("selectedCountry") || "gb";
      const { currency } = getConfigByCountryCode(selectedCountry);

      const converted = await convertPrice(pricing, "GBP", currency);
      const formatted = formatCurrency(converted, selectedCountry);

      setDisplayPrice(formatted);
    };

    updatePrice();
  }, [pricing, user?.country]);

  useEffect(() => {
    if (isLoggedIn) {
      refetch();
    }
  }, [isLoggedIn, refetch]);

  const handleClick = () => {
    navigate(`/templates/${_id}`);
  };

  // Converts plan key → readable name
  const formatMembershipName = (key) => {
    if (!key) return "";
    const words = key
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
    return words.join(" ");
  };

  // Validate if user has correct membership
  const checkMembershipAccess = (current_subscription, requiredKey) => {
    if (!current_subscription?.data) return { allowed: false };

    const userPackage = current_subscription.data.package;
    const userKey = userPackage.title?.toLowerCase().replace(/\s+/g, "_");
    const required = requiredKey?.toLowerCase().replace(/\s+/g, "_");

    const isActive =
      current_subscription.data.status === "active" &&
      current_subscription.data.cancel === false &&
      current_subscription.data.expiration === false;

    // Any active membership allowed
    if (required === "membership_only") {
      return { allowed: isActive };
    }

    // Specific membership required (fit_pro, fit_business)
    if (required !== userKey) {
      return {
        allowed: false,
        requiredReadable: formatMembershipName(required),
        currentReadable: userPackage.title,
      };
    }

    return { allowed: true };
  };

  const handleCTA = () => {
    if (!user?.role) {
      if (access_type === "free")
        return toast.info(t("toast.loginRequiredFree"));
      if (access_type === "membership")
        return toast.info(t("toast.loginRequiredMembership"));
      if (access_type === "paid")
        return toast.info(t("toast.loginRequiredPaid"));
      if (access_type === "custom")
        return toast.info(t("toast.loginRequiredCustom"));
      return;
    }

    // if (user.role === "learner") {
    //   if (access_type === "free")
    //     return toast.info(t("toast.creatorRequiredFree"));
    //   if (access_type === "membership")
    //     return toast.info(t("toast.creatorRequiredMembership"));
    //   if (access_type === "paid")
    //     return toast.info(t("toast.creatorRequiredPaid"));
    //   if (access_type === "custom")
    //     return toast.info(t("toast.creatorRequiredCustom"));
    //   return;
    // }

    if (user.role === "learner") {
      setShowRoleSwitchModal(true);
      return;
    }

    // const hasMembership =
    //   currentSuccess && current_subscription?.data?.status === "active";

    if (access_type === "free") {
      return navigate("/my-teaching", {
        state: {
          activeTab: "pills-pss",
          access_type,
          template_id: _id,
          title: title,
          template_price: pricing,
        },
      });
    }

    if (access_type === "membership") {
      const hasMembershipActive =
        currentSuccess &&
        current_subscription?.data?.status === "active" &&
        current_subscription?.data?.cancel === false &&
        current_subscription?.data?.expiration === false;

      if (!hasMembershipActive) {
        return toast.info(t("toast.membershipUpgradeNeeded"));
      }

      const result = checkMembershipAccess(
        current_subscription,
        membership_type,
      );

      if (!result.allowed) {
        const htmlMessage = t("toast.membershipPlanNotAllowed")
          .replace("{{required}}", `<b>${result.requiredReadable}</b>`)
          .replace("{{current}}", `<b>${result.currentReadable}</b>`);

        return toast.info(
          <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />,
        );
      }

      // Allowed → go to page
      return navigate("/my-teaching", {
        state: {
          activeTab: "pills-pss",
          access_type,
          title,
          template_id: _id,
        },
      });
    }

    if (access_type === "paid") {
      if (isProductPurchased) {
        return navigate("/my-teaching", {
          state: {
            activeTab: "pills-pss",
            access_type,
            title: title,
            template_id: _id,
          },
        });
      }

      const item = {
        _id,
        title,
        file,
        pricing,
        access_type,
        basketType: "template",
      };
      if (!cart.some((entry) => getBasketKey(entry) === getBasketKey(item)))
        dispatch(handleCart({ item }));
      return navigate("/cart");
    }

    if (access_type === "custom") {
      return toast.success(t("toast.customSuccess"));
    }
  };

  const badgeText =
    access_type === "paid"
      ? isProductPurchased
        ? t("active")
        : `${cartText("addToCart")} - ${displayPrice || pricing}`
      : access_type === "free"
        ? t("free")
        : t("membershipOnly");

  const handleSwitchRole = async () => {
    try {
      const payload = { role: "both" };

      await updateUser({
        id: user?._id,
        payload,
        params: { lang },
      }).unwrap();

      setShowRoleSwitchModal(false);
      setShowRoleSwitchSuccessModal(true);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || t("toast.roleUpdateError"));
    }
  };

  return (
    <>
      <div
        className="template-card d-flex flex-column"
        onClick={handleClick}
        style={{ cursor: "pointer" }}
      >
        <img
          src={file?.startsWith("http") ? file : images.home.template_default}
          alt={title}
          className="card-image"
        />

        <div
          className="card-body text-center d-flex flex-column justify-content-between"
          style={{ flex: 1 }}
        >
          <div>
            <h5
              className="card-title mb-1 text-truncate-2"
              style={{ minHeight: "44px" }}
            >
              {title_hook || title}
            </h5>

            <p
              className="card-subtitle mb-2"
              dangerouslySetInnerHTML={{
                __html: subtitle || "",
              }}
            />

            {duration && (
              <p
                className="duration-text mb-2"
                style={{ color: "#888", minHeight: "20px" }}
              >
                {duration}
              </p>
            )}
          </div>

          <div
            className="price-hover-wrapper"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{ position: "relative" }}
          >
            <Button
              text={badgeText}
              className={
                badgeText === t("free") ? "template-card-button-free" : ""
              }
              onClick={(e) => {
                e.stopPropagation();
                handleCTA();
              }}
            />

            {isHovering && pricing && access_type === "paid" && (
              <div className="price-tooltip">
                Price: {displayPrice || pricing}
              </div>
            )}
          </div>
        </div>
      </div>
      <SwitchToBothRoleModal
        show={showRoleSwitchModal}
        close={() => setShowRoleSwitchModal(false)}
        onConfirm={handleSwitchRole}
        isLoading={isLoadingUpdate}
        title={t("switchRoleModal.title")}
        subtitle={t("switchRoleModal.subtitle")}
        paragraph1={t("switchRoleModal.paragraph1")}
        paragraph2={t("switchRoleModal.paragraph2")}
        roleText={t("switchRoleModal.role")}
        yesText={t("switchRoleModal.yesBtn")}
        cancelText={t("switchRoleModal.cancelBtn")}
      />
      <RoleSwitchSuccessModal
        show={showRoleSwitchSuccessModal}
        close={() => setShowRoleSwitchSuccessModal(false)}
        title={t("roleSwitchConfirmation.title")}
        subtitle={t("roleSwitchConfirmation.subtitle")}
        continueText={t("roleSwitchConfirmation.continueBtn")}
      />
    </>
  );
};

export default memo(HeroSectionTemplateCard);
